import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import AllPeopleHeader from "@/components/molecules/table-headers/AllPeopleHeader";
import Table from "@/components/molecules/table/Table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import Person from "@/interfaces/Person";
import { useGetPeopleQuery } from "@/services/people";
import { faEdit, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { FC, useRef } from "react";
import { Link } from "react-router-dom";

const PeopleView: FC = () => {
  const [state, setState] = useTableState<Partial<TableState>>("people", {
    pagination: {
      pageIndex: 0,
      pageSize: 15,
    },
    globalFilter: "",
  });

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data } = useGetPeopleQuery({
    ...state.pagination,
    query: debouncedQuery,
    ...(state.sorting?.[0] && {
      sortBy: state.sorting[0].id,
      sortDirection: state.sorting[0].desc ? "desc" : "asc",
    }),
  });

  const columns = useRef<ColumnDef<Person>[]>([
    {
      header: "Actions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => (
        <>
          <Link to={`/people/${id}`} className="btn btn-primary btn-sm">
            <FontAwesomeIcon icon={faEye} />
          </Link>
          <Link to={`/people/${id}/edit`} className="btn btn-info btn-sm ms-2">
            <FontAwesomeIcon icon={faEdit} />
          </Link>
        </>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "#",
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => id,
      id: "id",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom",
      footer: (props) => props.column.id,
      accessorFn: ({ fullname }) => fullname,
      id: "fullname",
      cell: (info) => info.getValue(),
    },
    {
      header: "Email",
      footer: (props) => props.column.id,
      accessorFn: ({ email }) => email,
      id: "email",
      cell: (info) => info.getValue(),
    },
    {
      header: "Numéro\xa0de\xa0téléphone",
      footer: (props) => props.column.id,
      accessorFn: ({ phone }) => (phone ? <PhoneCallLink number={phone} /> : <UnknownBadge />),
      id: "phone",
      cell: (info) => info.getValue(),
    },
    {
      header: "Intitulé\xa0du\xa0poste",
      footer: (props) => props.column.id,
      accessorFn: ({ jobTitle }) => jobTitle,
      id: "jobTitle",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: (row) =>
        DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
  ]).current;

  return (
    <div className="formalys-page formalys-list-page">
      <Table
        resource="people"
        tableName="people"
        HeaderComponent={AllPeopleHeader}
        columns={columns}
        state={state}
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
      />
    </div>
  );
};

export default PeopleView;
